import { CommonModule } from '@angular/common';
import { Compiler, Component, NgModule, OnInit, ViewChild, ViewContainerRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';

import { ModalModule, ModalDirective } from 'ngx-bootstrap/modal';
import { PopoverModule } from 'ngx-bootstrap/popover';

import { AppComponent } from '../../app.component';
import { ResetPasswordMainComponent } from './reset-password-main.component';
import { ResetPasswordService } from '../../service/reset-password.service';
import { WebApiService } from '../../service/web-api.service';
import { DisplayService } from '../../service/display.service';
import { CheckItemService } from '../../service/checkItem.service';
import { CustomViewService } from '../../service/custom-view.service';
import { AuthService } from '../../service/auth.service';

import { MessageModalComponent } from '../message-modal/message-modal.component';
import { WaitModalComponent } from '../wait-modal/wait-modal.component';
import { ConfirmModalComponent } from '../confirm-modal/confirm-modal.component';
import { environment } from '../../../environments/environment';

declare let gtag: any; // グローバル変数gtagを解決

@Component({
  selector: 'app-reset-password',
  templateUrl: './reset-password.component.html',
  providers: [WebApiService, ResetPasswordService, CheckItemService, CustomViewService]
})
export class ResetPasswordComponent implements OnInit {

  constructor(private compiler: Compiler, private customViewService: CustomViewService,
    private resetPasswordService: ResetPasswordService, private appComponent: AppComponent,
    private displayService: DisplayService, private checkItemService: CheckItemService,
    public router: Router, private authService: AuthService, private activatedRoute: ActivatedRoute,
    public translateService: TranslateService) { }

  @ViewChild('waitModal', { static: false }) waitModal: WaitModalComponent;
  @ViewChild('messageModal', { static: false }) messageModal: MessageModalComponent;
  @ViewChild('confirmModal', { static: false }) confirmModal: ConfirmModalComponent;

  // 画面ID
  readonly SCREEN_ID = 'O03';

  // タイトルロゴ
  public dispZ0001: string;

  // ページ番号定義
  public pageNo: number;
  // 操作クラス定義
  public comp: ResetPasswordMainComponent;

  // テンプレートファイ名（HTML）
  public templateHtml: string;

  // テンプレートファイル（CSS）
  public templateCss: string;

  // 画面資材ルートURL
  public assetsBaseUrl: string;

  // 共通画面資材URL
  public assetsPortalBaseUrl: string;

  // サービス名
  public serviceName: string;

  // 画面項目定義
  public email: string;
  public password: string;
  public confirmPassword: string;

  @ViewChild('dest', { read: ViewContainerRef, static: false })
  public dest: ViewContainerRef;

  async ngOnInit() {
    // ページ番号をURLより取得
    const urlPageNo = +this.activatedRoute.snapshot.params['pageNo'];

    if (this.appComponent.dispZ0000 === undefined) {
      await this.appComponent.getDisplayInfo();
    }

    // 読み込まれるHTML名の取得
    this.serviceName = '';
    if (this.appComponent.dispZ0000 === '') {
      this.serviceName = 'portal';
    } else {
      this.serviceName = this.appComponent.dispZ0000;
    }

    this.assetsBaseUrl = environment.storageBaseUrl + '/' + this.serviceName + '/adume';
    this.assetsPortalBaseUrl = environment.storageBaseUrl + '/portal';
    this.templateHtml = this.assetsBaseUrl + '/html/reset-password/' + this.serviceName + '_reset-password';
    this.templateCss = this.assetsBaseUrl + '/css/' + this.serviceName + '_reset-password.css';

    // 画面カスタマイズ情報
    await this.displayService.execAuthDisplayInfo(this.SCREEN_ID).subscribe(data => {
      this.dispZ0001 = '';
      if (data.displayInfo != null) {
        if (data.displayInfo.Z0001 != null) {
          this.dispZ0001 = data.displayInfo.Z0001;
        }
      }

      // 初期値の設定
      this.pageNo = 1;
      this.email = '';
      this.password = '';
      this.confirmPassword = '';

      // 引継ぎパラメータ設定
      if (data.initParameter != null) {
        if (!isNaN(urlPageNo)) { this.pageNo = urlPageNo; }
        if (data.initParameter.email != null) { this.email = data.initParameter.email; }
        if (data.initParameter.password != null) { this.password = data.initParameter.password; }
        if (data.initParameter.confirm_password != null) { this.confirmPassword = data.initParameter.confirm_password; }
      }

      // カスタムページ表示
      this.directInjection();
    });
  }

  /**
   * コンポーネントを生成し親コンポーネントに挿入する
   *
   */
  public async directInjection() {

    gtag('config', environment.analyticsId, {
      'page_path': '/html/reset-password/' + this.serviceName + '_reset-password' + '/' + this.pageNo,
      'page_title': 'reset_password',
    });

    gtag('config', environment.analyticsIdGa4, {
      'page_title': 'reset_password',
      'page_location': this.serviceName + '/reset-password/' + this.pageNo
    });

    // 元の要素をクリアする
    this.dest.clear();

    // テンプレートを取得
    let templateHtmlText = await this.customViewService.getText(this.templateHtml + '_' + this.pageNo + '.html').toPromise();
    let templateCssText = await this.customViewService.getText(this.templateCss).toPromise();
    templateCssText = templateCssText.replace(/{{assetsBaseUrl}}/g, this.assetsBaseUrl);
    templateHtmlText = templateHtmlText.replace(/{{assetsBaseUrl}}/g, this.assetsBaseUrl);
    templateHtmlText = templateHtmlText.replace(/{{assetsPortalBaseUrl}}/g, this.assetsPortalBaseUrl);

    // Angular コンポーネントを用意する
    @Component({
      selector: 'app-reset-password-main',
      template: templateHtmlText,
      styles: [templateCssText],
    })

    class ResetPasswordModuleComponent extends ResetPasswordMainComponent { }
    // Angular モジュールを用意する

    @NgModule({
      imports: [CommonModule, FormsModule, PopoverModule.forRoot()],
      declarations: [ResetPasswordModuleComponent],
      exports: [ResetPasswordModuleComponent]
    })
    class ResetPasswordModule {
    }

    // Angular モジュールとコンポーネントをコンパイルする
    const module = this.compiler.compileModuleAndAllComponentsSync(ResetPasswordModule);
    // Angular モジュールから ResetPasswordModuleComponent を生成するファクトリを取り出す
    const factory = module.componentFactories.find((c) => {
      return c.componentType === ResetPasswordModuleComponent;
    });

    // ResetPasswordModuleComponent を生成し this.dest に設定する
    const componentRef = this.dest.createComponent(factory);

    // componentのインスタンスを取得する。
    this.comp = (<ResetPasswordModuleComponent>componentRef.instance);
    this.comp.init(this, this.resetPasswordService, this.checkItemService, this.authService,
      this.waitModal, this.messageModal, this.confirmModal);
  }
}
